"use client";
import { jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AutocompleteField } from "./AutocompleteField.js";
import { buildAutocompleteDataAndListType, excludeNeighbourhoodRecentSearches, hasRecentSearches, hasRecommendedSearches, mergeRecentSearchesById, mergeTypeaheadRecentSearches, resolveClickListTypeForItem, resolveSelectedChildPosition } from "./AutocompleteSearchField.utils.js";
const MAX_AUTOCOMPLETE_RECENT_SEARCHES = 3;
function locationKey(item) {
    if (!item) return;
    return String(item.id ?? item.cityID ?? item.itemName ?? '');
}
const AI_SEARCH_BUTTON_LABEL = {
    VARIANT1: 'Ask',
    VARIANT2: 'AI Mode'
};
const AutocompleteSearchField_AutocompleteSearchField = /*#__PURE__*/ forwardRef(function({ typeAheadEndpoint = 'hotels', searchValue, onSearchValueChange, onItemSelect, onSelectItemName, initialSelectedItem, errorMessage, label, isMobile, showFilterChips = false, selectedFilterChips, onFilterChipsClear, showCurrentLocation = false, onGeolocationError, aiSearchButtonVariant, mergeRecentAndRecommendedSearches = false, preferRecentSearchesOnly = false, onRecentSearchImprovementImpression, mergeAbandonedViewedAndRecentSearches = false, onRecentSearchLogicUpdateImpression, neighbourhoodNestingEnabled = false, hasAISearch = false, enablePrefillFromLastSearch = false, hideRecentsAndRecommendations = false, renderTrigger, collisionAvoidance, adapter }, ref) {
    const { signedIn } = adapter.session;
    const { local: recentSearchesLocalStorage, fetched: recentSearchesFetched, isFetchedReady: areRecentSearchesReady, abandoned: abandonedSearchesFetched = [], isAbandonedReady: areAbandonedSearchesReady = true, viewed: viewedSearchesFetched = [], isViewedReady: areViewedSearchesReady = true } = adapter.recentSearches;
    const { items: recommendedSearches } = adapter.recommendedSearches;
    const geo = adapter.geolocation;
    const typeAheadHorizon = adapter.typeAheadResult;
    const isHotelEndpoint = 'hotels' === typeAheadEndpoint || 'hotelsV2' === typeAheadEndpoint;
    const displayFilterChips = !hasAISearch && showFilterChips;
    const isHotelMergeRecentSearchFlow = isHotelEndpoint && mergeRecentAndRecommendedSearches;
    const useImprovedRecentSearchFlow = isHotelEndpoint && (mergeRecentAndRecommendedSearches || preferRecentSearchesOnly);
    const shouldMergeTripActivityHotelRecents = isHotelEndpoint && !useImprovedRecentSearchFlow && mergeAbandonedViewedAndRecentSearches;
    const recentSearches = useMemo(()=>{
        if (hideRecentsAndRecommendations) return [];
        const filterHotelRecentSearches = (items)=>isHotelEndpoint ? excludeNeighbourhoodRecentSearches(items) : items;
        if (useImprovedRecentSearchFlow) {
            if (!areRecentSearchesReady) return [];
            if (hasRecentSearches(recentSearchesFetched)) return mergeRecentSearchesById(filterHotelRecentSearches(recentSearchesFetched), filterHotelRecentSearches(recentSearchesLocalStorage), MAX_AUTOCOMPLETE_RECENT_SEARCHES);
            return filterHotelRecentSearches(recentSearchesLocalStorage).slice(0, MAX_AUTOCOMPLETE_RECENT_SEARCHES);
        }
        if (shouldMergeTripActivityHotelRecents) {
            if (!areRecentSearchesReady || !areAbandonedSearchesReady || !areViewedSearchesReady) return [];
            return mergeTypeaheadRecentSearches([
                filterHotelRecentSearches(abandonedSearchesFetched),
                filterHotelRecentSearches(viewedSearchesFetched),
                filterHotelRecentSearches(recentSearchesFetched)
            ], MAX_AUTOCOMPLETE_RECENT_SEARCHES);
        }
        const recentsSource = isHotelEndpoint ? recentSearchesFetched : recentSearchesLocalStorage;
        return filterHotelRecentSearches(recentsSource).slice(0, MAX_AUTOCOMPLETE_RECENT_SEARCHES);
    }, [
        hideRecentsAndRecommendations,
        isHotelEndpoint,
        useImprovedRecentSearchFlow,
        shouldMergeTripActivityHotelRecents,
        areRecentSearchesReady,
        areAbandonedSearchesReady,
        areViewedSearchesReady,
        abandonedSearchesFetched,
        viewedSearchesFetched,
        recentSearchesFetched,
        recentSearchesLocalStorage
    ]);
    const [prefilledItem, setPrefilledItem] = useState(void 0);
    const didPrefillRef = useRef(false);
    const hasInteractedRef = useRef(false);
    const effectiveInitialSelectedItem = prefilledItem ?? initialSelectedItem;
    const displayRecommendedSearches = useMemo(()=>hideRecentsAndRecommendations || isHotelMergeRecentSearchFlow && !areRecentSearchesReady || preferRecentSearchesOnly ? [] : recommendedSearches, [
        hideRecentsAndRecommendations,
        isHotelMergeRecentSearchFlow,
        areRecentSearchesReady,
        preferRecentSearchesOnly,
        recommendedSearches
    ]);
    const didFireSelect = useRef(false);
    const didFireRecentSearchImprovementImpression = useRef(false);
    const didFireRecentSearchLogicUpdateImpression = useRef(false);
    const [autoKey, setAutoKey] = useState('auto-default');
    const lastEmittedKeyRef = useRef(locationKey(initialSelectedItem));
    const currentSearchQueryRef = useRef(initialSelectedItem?.itemName ?? '');
    useEffect(()=>{
        if (!showCurrentLocation) return;
        if ('resolved' !== geo.status || !geo.cityData) return;
        if (didFireSelect.current) return;
        didFireSelect.current = true;
        lastEmittedKeyRef.current = locationKey(geo.cityData);
        onItemSelect?.(geo.cityData);
        const nextKey = `geo-${geo.cityData.id ?? geo.cityData.cityID}`;
        queueMicrotask(()=>{
            setAutoKey(nextKey);
        });
    }, [
        geo.status,
        geo.cityData,
        showCurrentLocation,
        onItemSelect
    ]);
    useEffect(()=>{
        if (!showCurrentLocation || !onGeolocationError) return;
        if ('rejected' === geo.status) onGeolocationError(geo.displayText);
        else if ('idle' === geo.status || 'resolved' === geo.status) onGeolocationError(null);
    }, [
        geo.status,
        geo.displayText,
        showCurrentLocation,
        onGeolocationError
    ]);
    useEffect(()=>{
        if (didPrefillRef.current) return;
        if (!enablePrefillFromLastSearch || initialSelectedItem) return;
        if (hasInteractedRef.current) return;
        const lastSearchedItem = recentSearches[0];
        if (!lastSearchedItem) return;
        didPrefillRef.current = true;
        queueMicrotask(()=>{
            if (hasInteractedRef.current) return;
            currentSearchQueryRef.current = lastSearchedItem.itemName ?? '';
            lastEmittedKeyRef.current = locationKey(lastSearchedItem);
            onItemSelect?.(lastSearchedItem, {
                isPrefill: true
            });
            setPrefilledItem(lastSearchedItem);
            setAutoKey(`prefill-${lastSearchedItem.id ?? lastSearchedItem.cityID}`);
        });
    }, [
        enablePrefillFromLastSearch,
        initialSelectedItem,
        recentSearches,
        onItemSelect
    ]);
    useEffect(()=>{
        const incomingKey = locationKey(initialSelectedItem);
        if (incomingKey === lastEmittedKeyRef.current) return;
        lastEmittedKeyRef.current = incomingKey;
        queueMicrotask(()=>{
            setPrefilledItem(void 0);
            setAutoKey(`external-${incomingKey ?? 'empty'}`);
            currentSearchQueryRef.current = initialSelectedItem?.itemName ?? '';
            onSearchValueChange('');
        });
    }, [
        initialSelectedItem,
        onSearchValueChange
    ]);
    const { autocompleteData: baseAutocompleteData, listType } = useMemo(()=>buildAutocompleteDataAndListType({
            autocompleteValue: searchValue,
            mergeRecentAndRecommendedSearches,
            preferRecentSearchesOnly,
            recentSearches,
            recommendedSearches: displayRecommendedSearches,
            typeAheadItems: typeAheadHorizon.items
        }), [
        mergeRecentAndRecommendedSearches,
        preferRecentSearchesOnly,
        recentSearches,
        displayRecommendedSearches,
        typeAheadHorizon.items,
        searchValue
    ]);
    const autocompleteData = useMemo(()=>{
        if (!(showCurrentLocation && isMobile)) return baseAutocompleteData;
        const currentLocationItem = {
            ...geo.cityData,
            content: 'Current Location',
            displayLine1: geo.displayText,
            itemName: geo.cityData?.itemName || '',
            type: 'GEO'
        };
        return [
            currentLocationItem,
            ...baseAutocompleteData
        ];
    }, [
        geo.cityData,
        geo.displayText,
        baseAutocompleteData,
        isMobile,
        showCurrentLocation
    ]);
    const firstData = autocompleteData[0];
    const currentCityId = firstData && 'cityID' in firstData && firstData.cityID ? firstData.cityID : void 0;
    const filterChipData = adapter.resolveFilterChipData?.(currentCityId);
    const prevCityIdRef = useRef(void 0);
    useEffect(()=>{
        const prevCityId = prevCityIdRef.current;
        if (void 0 !== prevCityId && prevCityId !== currentCityId) onFilterChipsClear?.();
        prevCityIdRef.current = currentCityId;
    }, [
        currentCityId,
        onFilterChipsClear
    ]);
    const handleTypeAheadFeedback = useCallback((item, { selectedPosition, selectedChildPosition, isRecentOrRecommendedSearch })=>{
        adapter.onTypeaheadFeedback?.({
            item,
            selectedPosition,
            selectedChildPosition,
            typeAheadEndpoint,
            neighbourhoodNestingEnabled,
            fetchedItems: typeAheadHorizon.items,
            apiDuration: typeAheadHorizon.duration,
            apiSource: typeAheadHorizon.source,
            isRecentOrRecommendedSearch
        });
    }, [
        adapter,
        typeAheadEndpoint,
        neighbourhoodNestingEnabled,
        typeAheadHorizon.items,
        typeAheadHorizon.duration,
        typeAheadHorizon.source
    ]);
    const handleInputValueChange = useCallback((inputValue)=>{
        hasInteractedRef.current = true;
        currentSearchQueryRef.current = inputValue;
        onSearchValueChange(inputValue);
        if ('' === inputValue) {
            lastEmittedKeyRef.current = void 0;
            onItemSelect?.(null);
            onSelectItemName?.(null);
        }
    }, [
        onItemSelect,
        onSelectItemName,
        onSearchValueChange
    ]);
    const handleSelectItem = useCallback((selectedItem, highlightedIndex)=>{
        if ('itemName' in selectedItem && 'string' == typeof selectedItem.itemName) currentSearchQueryRef.current = selectedItem.itemName;
        lastEmittedKeyRef.current = locationKey(selectedItem);
        if ('content' in selectedItem && 'Current Location' === selectedItem.content) {
            if (geo.cityData) onItemSelect?.(selectedItem);
            else geo.request();
            return;
        }
        const selectedLocation = selectedItem;
        onItemSelect?.(selectedLocation);
        const { id: selectedId, parentId, isIndented } = selectedLocation;
        const selectedChildPosition = resolveSelectedChildPosition({
            items: typeAheadHorizon.items,
            selectedId,
            parentId,
            isIndented,
            typeAheadEndpoint,
            neighbourhoodNestingEnabled
        });
        const resolveClickType = resolveClickListTypeForItem(selectedLocation, listType, recentSearches, displayRecommendedSearches);
        const resolvedName = adapter.onTypeaheadClick?.({
            item: selectedLocation,
            listType: resolveClickType,
            typeAheadEndpoint
        });
        onSelectItemName?.(resolvedName ?? null);
        const hasCurrentLocation = showCurrentLocation && isMobile;
        const adjustedIndex = hasCurrentLocation && null != highlightedIndex ? highlightedIndex - 1 : highlightedIndex;
        const isRecentOrRecommendedSearch = 'recent_searches' === resolveClickType || 'recommended' === resolveClickType || 'recent_and_recommended' === resolveClickType;
        handleTypeAheadFeedback(selectedLocation, {
            selectedPosition: adjustedIndex ?? null,
            selectedChildPosition,
            isRecentOrRecommendedSearch
        });
    }, [
        geo,
        isMobile,
        listType,
        recentSearches,
        displayRecommendedSearches,
        onItemSelect,
        showCurrentLocation,
        typeAheadEndpoint,
        handleTypeAheadFeedback,
        typeAheadHorizon.items,
        adapter,
        onSelectItemName,
        neighbourhoodNestingEnabled
    ]);
    const handleMenuOpen = useCallback(()=>{
        hasInteractedRef.current = true;
        const hasHotelRecents = useImprovedRecentSearchFlow ? areRecentSearchesReady && hasRecentSearches(recentSearches) : hasRecentSearches(recentSearches);
        const shouldFireImpression = Boolean(onRecentSearchImprovementImpression && !didFireRecentSearchImprovementImpression.current && isHotelEndpoint && signedIn && (hasHotelRecents || hasRecommendedSearches(recommendedSearches)));
        if (shouldFireImpression) {
            didFireRecentSearchImprovementImpression.current = true;
            onRecentSearchImprovementImpression?.();
        }
        if (onRecentSearchLogicUpdateImpression && isHotelEndpoint && !useImprovedRecentSearchFlow && !didFireRecentSearchLogicUpdateImpression.current) {
            didFireRecentSearchLogicUpdateImpression.current = true;
            onRecentSearchLogicUpdateImpression();
        }
        const updatedListType = 'typein' !== listType || searchValue ? listType : void 0;
        adapter.onAutocompleteOpen?.({
            typeAheadEndpoint,
            listType: updatedListType,
            hasRecents: hasHotelRecents,
            hasRecommended: hasRecommendedSearches(recommendedSearches)
        });
    }, [
        onRecentSearchImprovementImpression,
        onRecentSearchLogicUpdateImpression,
        isHotelEndpoint,
        signedIn,
        useImprovedRecentSearchFlow,
        areRecentSearchesReady,
        recentSearches,
        recommendedSearches,
        listType,
        searchValue,
        typeAheadEndpoint,
        adapter
    ]);
    const handleFilterChipClick = useCallback((item, selected)=>{
        adapter.onFilterChipToggle?.(item, selected);
    }, [
        adapter
    ]);
    const onAiSearchClick = useCallback(()=>{
        if (!aiSearchButtonVariant) return;
        adapter.onAiSearchClick?.({
            query: currentSearchQueryRef.current,
            variant: aiSearchButtonVariant
        });
    }, [
        aiSearchButtonVariant,
        adapter
    ]);
    const rightIconButtonProps = useMemo(()=>{
        if (!aiSearchButtonVariant) return;
        return {
            children: /*#__PURE__*/ jsx("span", {
                className: "text-primary-13",
                children: AI_SEARCH_BUTTON_LABEL[aiSearchButtonVariant]
            }),
            onClick: onAiSearchClick,
            type: 'tertiary',
            size: 'sm',
            iconLeft: 'AiSearch',
            slots: {
                textSlot: 'font-medium',
                iconSlot: 'relative -top-0.5'
            }
        };
    }, [
        aiSearchButtonVariant,
        onAiSearchClick
    ]);
    const getItemFirstLineSuffix = adapter.resolveItemFirstLineSuffix;
    return /*#__PURE__*/ jsx(AutocompleteField, {
        inputRef: ref,
        errorMessage: errorMessage,
        label: label,
        items: autocompleteData,
        onInputValueChange: handleInputValueChange,
        onSelectItem: handleSelectItem,
        defaultSelectedItem: effectiveInitialSelectedItem,
        onMenuOpen: handleMenuOpen,
        filterChipData: displayFilterChips ? filterChipData : void 0,
        handleFilterChipClick: handleFilterChipClick,
        selectedFilterChipData: selectedFilterChips,
        collisionAvoidance: collisionAvoidance,
        rightIconButtonProps: rightIconButtonProps,
        getItemFirstLineSuffix: getItemFirstLineSuffix,
        renderTrigger: renderTrigger
    }, autoKey);
});
export { AutocompleteSearchField_AutocompleteSearchField as AutocompleteSearchField };
